<template>
	<rmp-autocomplete
		v-model="model"
		:hide-details="hideDetails"
		:items="items"
		:label="noLabel ? undefined : (label ?? tc('fields.division.label'))"
		:tooltip-text="hint"
		:loading="loading"
		:multiple="multiple"
		single-line
		:placeholder="resolvedPlaceholder"
		item-text="title"
		item-value="value"
		:hide-checkbox="isOptionDisabled"
		:style="{ width }"
		:height="height"
		:disabled="disabled"
		:required="required"
		@update:query="onUpdateQuery"
	>
		<template #item="{ value: item }">
            <span :style="'padding-left: ' + (item.depth * 16) + 'px'">
                {{ item.title }}
            </span>
		</template>
	</rmp-autocomplete>
</template>

<script lang="ts" setup>
import RmpAutocomplete from "@rmp/core/components/fields/RmpAutocomplete.vue";
import { useI18n } from "vue-i18n-composable";
import { computed } from "vue";
import { DivisionsTreeNode } from "@rmp/core/utils/helpers";
import { normalizeDivisionIds } from "@rmp/core/utils/divisionScope";

const { tc } = useI18n();

type SelectItem = {
	value: string;
	title: string;
	rawTitle: string;
	type: DivisionsTreeNode["type"];
	depth: number;
	disabled: boolean;
};

interface Props {
	value?: string | string[] | undefined;
	items: DivisionsTreeNode[];
	loading?: boolean;
	onUpdateQuery?: (query: string) => void;
	noLabel?: boolean;
	disabled?: boolean;
	required?: boolean;
	multiple?: boolean;
	hideDetails?: boolean;
	width?: string;
	height?: string | number;
	branchSelectable?: boolean;
	// Залоченные подразделения: показываются с недоступным чекбоксом и всегда остаются в модели.
	lockedIds?: string[];
	label?: string;
	hint?: string;
	placeholder?: string;
}

const props = withDefaults(defineProps<Props>(), {
	lockedIds: () => []
});
const emit = defineEmits(["input", "update:query"]);

const model = computed({
	get: () => {
		// Залоченные подразделения всегда считаются выбранными, даже если модель формы их ещё не содержит.
		if(props.multiple && props.lockedIds.length) {
			return normalizeDivisionIds([...(Array.isArray(props.value) ? props.value : []), ...props.lockedIds]);
		}
		return props.value ?? undefined;
	},
	set: (val) => {
		// Залоченные подразделения нельзя снять — всегда возвращаем их в модель.
		if(props.multiple && Array.isArray(val)) {
			emit("input", normalizeDivisionIds([...val, ...props.lockedIds]));
			return;
		}
		emit("input", val);
	}
});

function getNodeTitle(node: DivisionsTreeNode) {
	return node.data.division.name;
}

function flattenTree(nodes: DivisionsTreeNode[], depth = 0): SelectItem[] {
	const lockedSet = new Set(props.lockedIds);
	return nodes.flatMap((node) => {
		const rawTitle = getNodeTitle(node);
		
		const current: SelectItem = {
			value: node.id,
			rawTitle,
			title: rawTitle,
			type: node.type,
			depth,
			disabled: lockedSet.has(node.id)
		};
		
		const children = node.children?.length
			? flattenTree(node.children, depth + 1)
			: [];
		
		return [current, ...children];
	});
}

const items = computed<SelectItem[]>(() => flattenTree(props.items));

// Плейсхолдер показываем только когда ничего не выбрано, чтобы он не налезал на выбранные значения.
const hasSelection = computed(() => Array.isArray(props.value) ? props.value.length > 0 : Boolean(props.value));
const resolvedPlaceholder = computed(() => hasSelection.value
	? undefined
	: (props.placeholder ?? tc("fields.division.placeholder")));

// Возвращает признак залоченного элемента, чекбокс которого недоступен.
function isOptionDisabled(item: SelectItem): boolean {
	return item.disabled;
}

const onUpdateQuery = (query: string) => {
	props.onUpdateQuery?.(query);
	emit("update:query", query);
};
</script>
